import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { User, ShieldCheck, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

/**
 * Account menu for signed-in product owners, shown in the site header.
 *
 * Only product owners get this: they are the only role with somewhere to go
 * (the admin console). Everyone else, signed in or not, sees the header unchanged,
 * so the public site never hints at staff tooling.
 */
const MENU_LINKS = [
  { label: 'Profile', icon: User, href: '/profile' },
  { label: 'Admin console', icon: ShieldCheck, href: '/admin/submissions' },
];

/** "Jane Smith" → "JS"; falls back to the first letter of the email. */
function initialsOf(user) {
  const name = (user.full_name || '').trim();
  if (name && name.includes('@') === false) {
    return name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  }
  return (user.email || '?')[0].toUpperCase();
}

function useSignOutToHome() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return async () => {
    await logout();
    navigate('/');
  };
}

export default function UserMenu() {
  const { user, isProductOwner } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const signOut = useSignOutToHome();

  // Close on outside click or Escape, mirroring the header's region picker.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user || !isProductOwner) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.email || 'Account'}
        className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground text-xs font-bold flex items-center justify-center shadow-md shadow-primary/25 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-opacity"
      >
        {initialsOf(user)}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            aria-label="Account"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-60 bg-card border border-border/40 rounded-xl shadow-xl p-2 z-50"
          >
            <div className="px-2 pb-2 mb-1 border-b border-border/40">
              <p className="text-sm font-semibold text-foreground truncate">{user.full_name || 'Product owner'}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
            {MENU_LINKS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm text-foreground hover:bg-muted/60 transition-colors"
              >
                <item.icon className="w-4 h-4 text-primary" />
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              role="menuitem"
              onClick={signOut}
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm text-foreground hover:bg-muted/60 transition-colors text-left"
            >
              <LogOut className="w-4 h-4 text-muted-foreground" />
              Log out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** The same links as a flat list, for the header's mobile panel. */
export function MobileUserLinks({ onNavigate }) {
  const { user, isProductOwner } = useAuth();
  const signOut = useSignOutToHome();

  if (!user || !isProductOwner) return null;

  return (
    <div className="pt-3 border-t border-border/40">
      <p className="px-4 pb-1 text-xs font-medium text-muted-foreground truncate">{user.email}</p>
      {MENU_LINKS.map((item) => (
        <Link
          key={item.href}
          to={item.href}
          onClick={onNavigate}
          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 rounded-lg"
        >
          <item.icon className="w-4 h-4 text-primary" />
          {item.label}
        </Link>
      ))}
      <button
        type="button"
        onClick={() => { onNavigate?.(); signOut(); }}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted/50 rounded-lg text-left"
      >
        <LogOut className="w-4 h-4 text-muted-foreground" />
        Log out
      </button>
    </div>
  );
}
